import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PropertyDetails = ({ bookingData, services, onUpdate }) => {
  const selectedService = services.find(s => s.id === bookingData.serviceId);

  const renderInputFields = () => {
    if (!selectedService) return null;

    if (selectedService.input_type === 'rooms') {
      return (
        <div>
          <Label htmlFor="rooms">Number of Rooms *</Label>
          <Input
            id="rooms"
            type="number"
            min="1"
            max="25"
            value={bookingData.rooms}
            onChange={(e) => onUpdate({ rooms: parseInt(e.target.value) })}
            data-testid="rooms-input"
            className="mt-1"
          />
          {bookingData.rooms > 25 && (
            <div className="mt-4 bg-amber-50 border border-amber-300 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-900 mb-2">Large Property - Custom Quote Required</p>
              <p className="text-sm text-amber-800 mb-3">
                For properties with more than 25 rooms or multiple properties, pricing is available on request due to volume discounts and complexity.
              </p>
              <a 
                href="/contact" 
                className="inline-block bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
              >
                Contact Us for a Tailored Quote
              </a>
            </div>
          )}
        </div>
      );
    }

    if (selectedService.input_type === 'sqm') {
      return (
        <div>
          <Label htmlFor="sqm">Floor Area (m²) *</Label>
          <Input
            id="sqm"
            type="number"
            min="1"
            step="0.1"
            value={bookingData.sqm}
            onChange={(e) => onUpdate({ sqm: parseFloat(e.target.value) })}
            data-testid="sqm-input"
            className="mt-1"
          />
        </div>
      );
    }

    if (selectedService.id === 'u_values') {
      return (
        <div>
          <Label className="mb-3 block">Select U-Value Calculations *</Label>
          <div className="space-y-3">
            {['wall', 'floor', 'roof'].map(option => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`u-value-${option}`}
                  checked={bookingData.uValueOptions?.[option] || false}
                  onCheckedChange={(checked) => {
                    onUpdate({
                      uValueOptions: { ...bookingData.uValueOptions, [option]: checked }
                    });
                  }}
                  data-testid={`u-value-${option}-checkbox`}
                />
                <Label htmlFor={`u-value-${option}`} className="font-normal cursor-pointer">
                  {option.charAt(0).toUpperCase() + option.slice(1)} (£65)
                </Label>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (selectedService.price_on_request) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            This service requires a custom quote. Please provide as much detail as possible, and we will contact you with pricing.
          </p>
        </div>
      );
    }

    return null;
  };

  const renderExtras = () => {
    if (bookingData.serviceId === 'sap_calculation') {
      return (
        <div>
          <Label className="mb-3 block">Optional Extras</Label>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="repeat-unit"
                checked={bookingData.extras?.repeat_unit || false}
                onCheckedChange={(checked) => {
                  onUpdate({ extras: { ...bookingData.extras, repeat_unit: checked } });
                }}
                data-testid="extra-repeat-unit-checkbox"
              />
              <Label htmlFor="repeat-unit" className="font-normal cursor-pointer">
                Repeat Unit (£65)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="epc-lodgement"
                checked={bookingData.extras?.epc_lodgement || false}
                onCheckedChange={(checked) => {
                  onUpdate({ extras: { ...bookingData.extras, epc_lodgement: checked } });
                }}
                data-testid="extra-epc-lodgement-checkbox"
              />
              <Label htmlFor="epc-lodgement" className="font-normal cursor-pointer">
                EPC Lodgement (£65)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="extension-sap"
                checked={bookingData.extras?.extension_sap || false}
                onCheckedChange={(checked) => {
                  onUpdate({ extras: { ...bookingData.extras, extension_sap: checked } });
                }}
                data-testid="extra-extension-sap-checkbox"
              />
              <Label htmlFor="extension-sap" className="font-normal cursor-pointer">
                Extension SAP (£120)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="part-o"
                checked={bookingData.extras?.part_o || false}
                onCheckedChange={(checked) => {
                  onUpdate({ extras: { ...bookingData.extras, part_o: checked } });
                }}
                data-testid="extra-part-o-checkbox"
              />
              <Label htmlFor="part-o" className="font-normal cursor-pointer">
                Simplified Part O (£150)
              </Label>
            </div>
          </div>
        </div>
      );
    }

    if (bookingData.serviceId === 'leasehold_plans' || bookingData.serviceId === 'marketing_plans') {
      return (
        <div>
          <Label className="mb-3 block">Optional Extras</Label>
          <div className="space-y-4">
            <div>
              <Label htmlFor="elevations" className="mb-2 block">Number of Elevations (£65 each, max 10)</Label>
              <Input
                id="elevations"
                type="number"
                min="0"
                max="10"
                value={bookingData.elevations || 0}
                onChange={(e) => onUpdate({ elevations: parseInt(e.target.value) || 0 })}
                data-testid="elevations-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="sections" className="mb-2 block">Number of Sections (£65 each, max 5)</Label>
              <Input
                id="sections"
                type="number"
                min="0"
                max="5"
                value={bookingData.sections || 0}
                onChange={(e) => onUpdate({ sections: parseInt(e.target.value) || 0 })}
                data-testid="sections-input"
                className="mt-1"
              />
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div data-testid="property-details-step">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Property Details</h2>
      <p className="text-gray-600 mb-6">Tell us about your property</p>

      <div className="space-y-6">
        <div>
          <Label htmlFor="address-line1">Address Line 1 *</Label>
          <Input
            id="address-line1"
            placeholder="House number and street name"
            value={bookingData.addressLine1 || ''}
            onChange={(e) => onUpdate({ addressLine1: e.target.value })}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="address-line2">Address Line 2 (Optional)</Label>
          <Input
            id="address-line2"
            placeholder="Apartment, suite, etc."
            value={bookingData.addressLine2 || ''}
            onChange={(e) => onUpdate({ addressLine2: e.target.value })}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="city">Town/City *</Label>
          <Input
            id="city"
            placeholder="Exeter"
            value={bookingData.city || ''}
            onChange={(e) => onUpdate({ city: e.target.value })}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="postcode">Postcode *</Label>
          <Input
            id="postcode"
            placeholder="EX1 2AB"
            value={bookingData.postcode}
            onChange={(e) => onUpdate({ postcode: e.target.value.toUpperCase() })}
            data-testid="postcode-input"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="property-type">Property Type *</Label>
          <Select
            value={bookingData.propertyType}
            onValueChange={(value) => onUpdate({ propertyType: value })}
          >
            <SelectTrigger data-testid="property-type-select" className="mt-1">
              <SelectValue placeholder="Select property type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="detached">Detached House</SelectItem>
              <SelectItem value="semi-detached">Semi-Detached House</SelectItem>
              <SelectItem value="terraced">Terraced House</SelectItem>
              <SelectItem value="bungalow">Bungalow</SelectItem>
              <SelectItem value="flat">Flat/Apartment</SelectItem>
              <SelectItem value="commercial">Commercial Property</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {renderInputFields()}
        {renderExtras()}
      </div>
    </div>
  );
};

export default PropertyDetails;